import React from 'react';
import { Link } from 'react-router-dom';
import { Printer, Video, Music, Sparkles, Download } from 'lucide-react';

const BALOAN = "https://www.casasamba.com/wp-content/uploads/2026/09/8cti03q3_mestre_curtis_playing_the_baloan_v1-540p.mp4";
const OMUKO = "https://www.casasamba.com/wp-content/uploads/2026/09/6vnt2gtv_omuko_playing_v1-360p.mp4";
const POSTER = "https://www.casasamba.com/wp-content/uploads/2026/09/ancestors_awaken_final.jpg";

export default function AncestorsFlyer() {
  return (
    <div className="min-h-screen bg-black text-white print:bg-white print:text-black">
      <style>{`
        /* Hide third-party widgets (chat bubble, reCAPTCHA badge) on this page — screen AND print */
        .grecaptcha-badge,
        iframe[src*="recaptcha"],
        iframe[title*="chat"],
        [class*="chat-widget"],
        [id*="chat-widget"],
        [class*="Chat"] [role="button"][aria-label*="chat" i],
        [aria-label*="Open chat" i],
        [data-testid="page-edit-toolbar"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
        @media print {
          @page { size: letter portrait; margin: 0.3in; }
          html, body { background: white !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .print-hide { display: none !important; }
          .print-poster-wrap { max-height: 10.4in; overflow: hidden; page-break-inside: avoid; page-break-after: avoid; }
          .print-poster { max-height: 10.4in !important; width: auto !important; margin: 0 auto !important; display: block; }
          .print-overlay { position: absolute !important; }
          /* Kill the gradient-text effect for print — it prints as a solid yellow rectangle. */
          .flyer-title-print {
            background: none !important;
            -webkit-background-clip: initial !important;
            background-clip: initial !important;
            -webkit-text-fill-color: #fbbf24 !important;
            color: #fbbf24 !important;
            text-shadow: 0 2px 6px rgba(0,0,0,0.6);
          }
        }
      `}</style>
      <div className="max-w-4xl mx-auto px-6 py-10 print:py-0 print:px-0 print:max-w-none">
        <div className="flex justify-between items-center mb-6 print-hide">
          <Link to="/ancestral-project" className="text-yellow-400 text-sm font-bold">← Proposal</Link>
          <div className="flex gap-2">
            <a
              href={POSTER}
              download="Ancestors_Awaken_Poster.png"
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-full text-sm font-bold hover:bg-yellow-400"
            >
              <Download className="w-4 h-4" /> Download Poster (PNG)
            </a>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 text-white rounded-full text-sm font-bold hover:bg-white/20"
            >
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>
        </div>

        {/* AI POSTER */}
        <div className="relative rounded-3xl overflow-hidden shadow-[0_25px_80px_-15px_rgba(230,120,23,0.6)] print:shadow-none print:rounded-none print-poster-wrap">
          <img
            src={POSTER}
            alt="Mestre Curtis in his handmade regalia playing his kora, surrounded by his ancestral string instruments, symphony orchestra behind"
            className="w-full block print-poster"
          />

          {/* TOP overlay — event date & billing (shows on-screen AND on print) */}
          <div className="absolute inset-x-0 top-0 p-4 md:p-6 text-center bg-gradient-to-b from-black/90 via-black/60 to-transparent print-overlay">
            <p className="text-yellow-300 text-[10px] md:text-xs font-black tracking-[0.5em] uppercase mb-1">
              <Sparkles className="w-3 h-3 inline mr-1" /> Casa Samba Presents
            </p>
            <h1
              className="flyer-title-print text-3xl md:text-5xl font-black leading-tight"
              style={{
                background: 'linear-gradient(135deg,#fde68a 0%,#fbbf24 50%,#dc2626 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              Ancestors Awaken
            </h1>
            <p className="text-white text-xs md:text-base mt-1 italic font-light drop-shadow-lg">
              An Afro-Diasporic Musical Convergence
            </p>
            <p className="text-yellow-300 text-[10px] md:text-sm mt-2 font-black tracking-[0.3em] uppercase drop-shadow-lg">
              New Orleans · 2026 · One Night Only
            </p>
          </div>

          {/* BOTTOM overlay — billing (shows on-screen AND on print) */}
          <div className="absolute inset-x-0 bottom-0 p-3 md:p-5 text-center bg-gradient-to-t from-black/95 via-black/70 to-transparent print-overlay">
            <p className="text-white/95 text-[9px] md:text-xs tracking-[0.25em] uppercase font-bold drop-shadow-lg">
              MESTRE CURTIS PIERRE · CONDUCTOR · ORCHESTRA
            </p>
            <p className="text-white/85 text-[9px] md:text-xs tracking-[0.25em] uppercase font-bold mt-1 drop-shadow-lg">
              7 DRUMMERS · 3 DANCERS · 1 MASQUERADE
            </p>
            <p className="text-white/60 text-[8px] md:text-[10px] mt-2 tracking-widest uppercase">
              Escola de Samba Casa Samba · Est. 1987 · NOTCF Grant Applicant
            </p>
          </div>
        </div>

        {/* REAL FOOTAGE */}
        <div className="mt-10 print-hide">
          <div className="text-center mb-6">
            <p className="text-yellow-400 text-xs font-bold tracking-[0.35em] uppercase mb-1">
              <Video className="w-3 h-3 inline mr-1" /> Live Footage
            </p>
            <h2 className="text-2xl md:text-3xl font-black">Mestre Curtis on His Own Ancestral Instruments</h2>
            <p className="text-white/60 text-sm mt-2">Real recordings of what the audience will hear on the night — Baloan and Omuko, both played by Mestre Curtis.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-neutral-900">
              <video src={BALOAN} controls playsInline preload="metadata" className="w-full aspect-video bg-black" />
              <div className="p-4">
                <p className="text-yellow-400 text-[10px] font-bold tracking-widest uppercase">Instrument</p>
                <h3 className="text-lg font-black">Baloan</h3>
                <p className="text-white/60 text-sm">West-African wooden mallet instrument · Mestre Curtis, solo.</p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-neutral-900">
              <video src={OMUKO} controls playsInline preload="metadata" className="w-full aspect-video bg-black" />
              <div className="p-4">
                <p className="text-yellow-400 text-[10px] font-bold tracking-widest uppercase">Instrument</p>
                <h3 className="text-lg font-black">Omuko</h3>
                <p className="text-white/60 text-sm">Ancestral African string instrument · Mestre Curtis, solo.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 text-center print-hide">
          <p className="text-white/60 text-sm max-w-2xl mx-auto">
            <Music className="w-4 h-4 inline mr-1 text-yellow-400" />
            The AI-rendered poster above previews the vision. The videos are the real thing — what the ancestral instruments actually sound like in Mestre Curtis's hands.
          </p>
          <p className="text-[10px] tracking-widest uppercase text-white/40 mt-6">Escola de Samba Casa Samba · Est. 1987 · NOTCF Grant Applicant</p>
        </div>
      </div>
    </div>
  );
}

